import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiDownload, FiFileText, FiImage, FiDatabase, FiMail, 
  FiShare2, FiCalendar, FiFilter, FiSettings, FiCheck,
  FiLoader, FiAlertCircle, FiX
} from 'react-icons/fi';
import { format } from 'date-fns';
import { api } from '../utils/api';
import toast from 'react-hot-toast';

export interface ExportData {
  title: string;
  data: any[];
  metadata?: {
    generatedAt: Date;
    filters?: Record<string, any>;
    totalRecords?: number;
    source?: string;
  };
}

export interface ExportConfig {
  format: 'pdf' | 'excel' | 'csv' | 'json' | 'png' | 'svg';
  filename?: string;
  options?: {
    // PDF options
    orientation?: 'portrait' | 'landscape';
    pageSize?: 'A4' | 'A3' | 'letter';
    includeCharts?: boolean;
    includeMetadata?: boolean;
    
    // Excel options
    sheetName?: string;
    includeHeaders?: boolean;
    autoFilter?: boolean;
    formatting?: boolean;
    
    // CSV options
    delimiter?: ',' | ';' | '\t';
    encoding?: 'utf-8' | 'latin1';
    includeCsvHeaders?: boolean;
    
    // Image options
    quality?: number; // 0-100 for JPEG
    width?: number;
    height?: number;
    backgroundColor?: string;
  };
}

interface ExportToolsProps {
  data: ExportData;
  onExport?: (config: ExportConfig) => Promise<void>;
  onSchedule?: (config: ExportConfig & { schedule: any }) => Promise<void>;
  onShare?: (config: ExportConfig & { recipients: string[] }) => Promise<void>;
  availableFormats?: ExportConfig['format'][];
  className?: string;
}

const ExportTools: React.FC<ExportToolsProps> = ({
  data,
  onExport,
  onSchedule,
  onShare,
  availableFormats = ['pdf', 'excel', 'csv', 'json', 'png'],
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'export' | 'schedule' | 'share'>('export');
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  
  // Export configuration
  const [exportConfig, setExportConfig] = useState<ExportConfig>({
    format: 'pdf',
    filename: `${data.title}_${format(new Date(), 'yyyy-MM-dd')}`
  });

  // Schedule configuration
  const [scheduleConfig, setScheduleConfig] = useState({
    frequency: 'daily',
    time: '09:00',
    timezone: 'UTC',
    email: '',
    enabled: true
  });

  // Share configuration
  const [shareConfig, setShareConfig] = useState({
    recipients: [''],
    subject: `Data Export: ${data.title}`,
    message: 'Please find the exported data attached.',
    includeLink: false
  });

  const exportRef = useRef<HTMLDivElement>(null);

  // Format options by type
  const formatOptions = {
    pdf: {
      icon: FiFileText,
      label: 'PDF Report',
      description: 'Professional report with charts and formatting',
      options: ['orientation', 'pageSize', 'includeCharts', 'includeMetadata']
    },
    excel: {
      icon: FiDatabase,
      label: 'Excel Spreadsheet',
      description: 'Structured data with formulas and formatting',
      options: ['sheetName', 'includeHeaders', 'autoFilter', 'formatting']
    },
    csv: {
      icon: FiFileText,
      label: 'CSV File',
      description: 'Raw data in comma-separated format',
      options: ['delimiter', 'encoding', 'includeHeaders']
    },
    json: {
      icon: FiDatabase,
      label: 'JSON Data',
      description: 'Machine-readable structured data',
      options: []
    },
    png: {
      icon: FiImage,
      label: 'PNG Image',
      description: 'High-quality image export',
      options: ['width', 'height', 'quality', 'backgroundColor']
    },
    svg: {
      icon: FiImage,
      label: 'SVG Vector',
      description: 'Scalable vector graphics',
      options: ['width', 'height']
    }
  };

  // Handle export
  const handleExport = async () => {
    if (!onExport) return;

    setIsExporting(true);
    setExportProgress(0);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setExportProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      await onExport(exportConfig);
      
      clearInterval(progressInterval);
      setExportProgress(100);
      
      setTimeout(() => {
        setIsExporting(false);
        setExportProgress(0);
        setIsOpen(false);
        toast.success('Export completed successfully');
      }, 500);
    } catch (error) {
      console.error('Export failed:', error);
      setIsExporting(false);
      setExportProgress(0);
      toast.error('Export failed. Please try again.');
    }
  };

  // Handle schedule
  const handleSchedule = async () => {
    if (!onSchedule) return;

    try {
      await onSchedule({
        ...exportConfig,
        schedule: scheduleConfig
      });
      toast.success('Export scheduled successfully');
      setIsOpen(false);
    } catch (error) {
      console.error('Schedule failed:', error);
      toast.error('Failed to schedule export');
    }
  };

  // Handle share
  const handleShare = async () => {
    if (!onShare) return;

    try {
      await onShare({
        ...exportConfig,
        recipients: shareConfig.recipients.filter(email => email.trim())
      });
      toast.success('Export shared successfully');
      setIsOpen(false);
    } catch (error) {
      console.error('Share failed:', error);
      toast.error('Failed to share export');
    }
  };

  // Add recipient
  const addRecipient = () => {
    setShareConfig(prev => ({
      ...prev,
      recipients: [...prev.recipients, '']
    }));
  };

  // Remove recipient
  const removeRecipient = (index: number) => {
    setShareConfig(prev => ({
      ...prev,
      recipients: prev.recipients.filter((_, i) => i !== index)
    }));
  };

  // Update recipient
  const updateRecipient = (index: number, email: string) => {
    setShareConfig(prev => ({
      ...prev,
      recipients: prev.recipients.map((r, i) => i === index ? email : r)
    }));
  };

  return (
    <div className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
      >
        <FiDownload size={16} />
        Export
      </button>

      {/* Export Modal */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setIsOpen(false)}
            />
            
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl bg-white rounded-xl shadow-2xl z-50 max-h-[90vh] overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Export Data</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {data.metadata?.totalRecords?.toLocaleString() || data.data.length} records • Generated {format(new Date(), 'MMM d, yyyy')}
                  </p>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                >
                  <FiX size={20} />
                </button>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200">
                <nav className="flex space-x-8 px-6">
                  {[
                    { id: 'export', label: 'Export', icon: FiDownload },
                    { id: 'schedule', label: 'Schedule', icon: FiCalendar },
                    { id: 'share', label: 'Share', icon: FiShare2 }
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as any)}
                      className={`flex items-center gap-2 py-4 border-b-2 font-medium text-sm transition-colors ${
                        activeTab === tab.id
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <tab.icon size={16} />
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>

              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {/* Export Tab */}
                {activeTab === 'export' && (
                  <div className="space-y-6">
                    {/* Format Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Export Format
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        {availableFormats.map(format => {
                          const option = formatOptions[format];
                          const Icon = option.icon;
                          
                          return (
                            <button
                              key={format}
                              onClick={() => setExportConfig(prev => ({ ...prev, format }))}
                              className={`p-4 rounded-lg border-2 text-left transition-colors ${
                                exportConfig.format === format
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex items-center gap-3 mb-2">
                                <Icon className={`${
                                  exportConfig.format === format ? 'text-blue-600' : 'text-gray-400'
                                }`} size={20} />
                                <span className="font-medium text-gray-900">
                                  {option.label}
                                </span>
                              </div>
                              <p className="text-xs text-gray-600">
                                {option.description}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Filename */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Filename
                      </label>
                      <input
                        type="text"
                        value={exportConfig.filename}
                        onChange={(e) => setExportConfig(prev => ({ 
                          ...prev, 
                          filename: e.target.value 
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter filename"
                      />
                    </div>

                    {/* Format-specific options */}
                    {exportConfig.format === 'pdf' && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Orientation
                          </label>
                          <select
                            value={exportConfig.options?.orientation || 'portrait'}
                            onChange={(e) => setExportConfig(prev => ({
                              ...prev,
                              options: { ...prev.options, orientation: e.target.value as any }
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="portrait">Portrait</option>
                            <option value="landscape">Landscape</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Page Size
                          </label>
                          <select
                            value={exportConfig.options?.pageSize || 'A4'}
                            onChange={(e) => setExportConfig(prev => ({
                              ...prev,
                              options: { ...prev.options, pageSize: e.target.value as any }
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="A4">A4</option>
                            <option value="A3">A3</option>
                            <option value="letter">Letter</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Schedule Tab */}
                {activeTab === 'schedule' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Frequency
                        </label>
                        <select
                          value={scheduleConfig.frequency}
                          onChange={(e) => setScheduleConfig(prev => ({ 
                            ...prev, 
                            frequency: e.target.value 
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Time
                        </label>
                        <input
                          type="time"
                          value={scheduleConfig.time}
                          onChange={(e) => setScheduleConfig(prev => ({ 
                            ...prev, 
                            time: e.target.value 
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Recipient
                      </label>
                      <input
                        type="email"
                        value={scheduleConfig.email}
                        onChange={(e) => setScheduleConfig(prev => ({ 
                          ...prev, 
                          email: e.target.value 
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter email address"
                      />
                    </div>
                  </div>
                )}

                {/* Share Tab */}
                {activeTab === 'share' && (
                  <div className="space-y-6">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Recipients
                        </label>
                        <button
                          onClick={addRecipient}
                          className="text-sm text-blue-600 hover:text-blue-700"
                        >
                          + Add recipient
                        </button>
                      </div>
                      
                      <div className="space-y-2">
                        {shareConfig.recipients.map((email, index) => (
                          <div key={index} className="flex gap-2">
                            <input
                              type="email"
                              value={email}
                              onChange={(e) => updateRecipient(index, e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Enter email address"
                            />
                            {shareConfig.recipients.length > 1 && (
                              <button
                                onClick={() => removeRecipient(index)}
                                className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-100"
                              >
                                <FiX size={16} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Subject
                      </label>
                      <input
                        type="text"
                        value={shareConfig.subject}
                        onChange={(e) => setShareConfig(prev => ({ 
                          ...prev, 
                          subject: e.target.value 
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Message
                      </label>
                      <textarea
                        value={shareConfig.message}
                        onChange={(e) => setShareConfig(prev => ({ 
                          ...prev, 
                          message: e.target.value 
                        }))}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
                <div className="text-sm text-gray-600">
                  {activeTab === 'export' && 'Export will be downloaded to your device'}
                  {activeTab === 'schedule' && 'Scheduled exports will be sent via email'}
                  {activeTab === 'share' && 'Recipients will receive the export via email'}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setIsOpen(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  
                  <button
                    onClick={() => {
                      if (activeTab === 'export') handleExport();
                      if (activeTab === 'schedule') handleSchedule();
                      if (activeTab === 'share') handleShare();
                    }}
                    disabled={isExporting}
                    className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    {isExporting ? (
                      <>
                        <FiLoader className="animate-spin" size={16} />
                        Exporting... ({exportProgress}%)
                      </>
                    ) : (
                      <>
                        {activeTab === 'export' && <FiDownload size={16} />}
                        {activeTab === 'schedule' && <FiCalendar size={16} />}
                        {activeTab === 'share' && <FiMail size={16} />}
                        {activeTab === 'export' && 'Export Now'}
                        {activeTab === 'schedule' && 'Schedule Export'}
                        {activeTab === 'share' && 'Share Now'}
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Progress Bar */}
              {isExporting && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200">
                  <motion.div
                    className="h-full bg-blue-500"
                    initial={{ width: 0 }}
                    animate={{ width: `${exportProgress}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ExportTools;